/* eslint-disable react/destructuring-assignment */
import { ColorName, Icon, IconName, IconProps } from '@zapier/design-system';
import { clsx } from 'clsx';
import React from 'react';
import {
  Button,
  ButtonProps,
  Link,
  LinkProps,
  PressEvent,
} from 'react-aria-components';

import styles from './StatusChip.module.scss';

type BaseStatusChipProps = {
  /** Handler called when the press is released over the `StatusChip`. */
  onPress?: (event: PressEvent) => void;
  /** The size of the `StatusChip`. */
  size?: 'compact' | 'small' | 'medium';
  /** The visual style of the `StatusChip`. */
  variant?: 'success' | 'warning' | 'error';
  /**
   * Whether the `StatusChip` is disabled.
   * @default false
   */
  isDisabled?: boolean;
};

type ButtonStatusChipProps = BaseStatusChipProps & {
  href?: undefined;
  hrefLang?: undefined;
  children?: ButtonProps['children'];
  className?: string;
  style?: React.CSSProperties;
  target?: undefined;
};

type LinkStatusChipProps = BaseStatusChipProps & {
  /** The children of the `StatusChip`. A function may be provided to alter the children based on the chip's state. */
  children?: LinkProps['children'];
  /** The CSS `className` for the chip. */
  className?: string;
  /** A URL to link. When passed, `StatusChip` will be rendered as a Link instead of a button.  */
  href: string;
  /** Hints at the human language of the linked URL. Only used if `href` prop is passed. */
  hrefLang?: string;
  /** The inline style for the chip. */
  style?: React.CSSProperties;
  /** The target window. Only used if `href` prop is passed. */
  target?: LinkProps['target'];
};

export type StatusChipProps = ButtonStatusChipProps | LinkStatusChipProps;

function isLinkProps(props: StatusChipProps): props is LinkStatusChipProps {
  return typeof props.href === 'string';
}

/**
 * StatusChip is used to display the status of an element while offering a path to know more about it.
 */
export const StatusChip = (props: StatusChipProps) => {
  const {
    className,
    href,
    hrefLang,
    isDisabled = false,
    onPress,
    size,
    style,
    target,
    variant,
  } = props;

  const getIconProps = (): {
    color: ColorName | undefined;
    name: IconName;
    size: IconProps['size'];
  } => {
    const iconSize =
      size === 'medium' ? 'var(--zds-size-20)' : 'var(--zds-size-16)';

    let iconProps: {
      name: IconName;
      color: ColorName | undefined;
      size: IconProps['size'];
    };

    if (variant === 'warning') {
      iconProps = {
        name: isDisabled ? 'alertCircleFill' : 'alertCircleColor',
        color: undefined,
        size: iconSize,
      };
    } else if (variant === 'error') {
      iconProps = {
        name: 'formXCircle',
        color: isDisabled ? 'GrayWarm6' : 'StatusErrorStronger',
        size: iconSize,
      };
    } else {
      iconProps = {
        name: 'formCheckCircle',
        color: isDisabled ? 'GrayWarm6' : 'StatusSuccessStronger',
        size: iconSize,
      };
    }

    return iconProps;
  };

  if (isLinkProps(props)) {
    return (
      <Link
        className={clsx(styles.root, className)}
        data-size={size}
        data-variant
        data-zds
        isDisabled={isDisabled}
        href={href}
        hrefLang={hrefLang}
        onPress={onPress}
        target={target}
      >
        {(linkRenderProps) => (
          <>
            <Icon {...getIconProps()} />
            {typeof props.children === 'function'
              ? props.children(linkRenderProps)
              : props.children}
          </>
        )}
      </Link>
    );
  }

  return (
    <Button
      className={clsx(styles.root, className)}
      data-size={size}
      data-variant={variant}
      data-zds
      isDisabled={isDisabled}
      onPress={onPress}
      style={style}
    >
      {(buttonRenderProps) => (
        <>
          <Icon {...getIconProps()} />
          {typeof props.children === 'function'
            ? props.children(buttonRenderProps) // If we destructure children, TS will believe that `children`'s render props are LinkRenderProps instead of ButtonRenderProps.
            : props.children}
        </>
      )}
    </Button>
  );
};
